import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Box, Typography, CircularProgress, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import WikiCategoryCard from './WikiCategoryCard';
import { SPACING } from '../../../utils/responsive';
import { TEXT_COLORS } from '../../../utils/colors';

export interface CategoryItem {
  id: number;
  name: string;
  source: string;
  path: string;
  // Hierarchy info (optional, for professions with parent-child relationships)
  hierarchyColor?: string;
  hierarchyDepth?: number;
  hierarchyHasChildren?: boolean;
  hierarchyIsRoot?: boolean;
}

interface WikiCategoryGridProps {
  categories: CategoryItem[];
  loading?: boolean;
  emptyMessage?: string;
}

/** Запас снизу: карточка на hover поднимается на 6px — без запаса её верх режет край скролла. */
const HOVER_RESERVE = 10;
/**
 * Ниже этой высоты карточку не сжимаем — вместо этого появляется скролл.
 * 140 (а не 160) подобрано по реальным страницам: два ряда по 9-12 карточек влезают целиком,
 * при 160 они вылезали на пару десятков пикселей и включали скролл почти-влезающего ряда.
 */
const MIN_CARD_HEIGHT = { xs: 120, sm: 140 };

const WikiCategoryGrid: React.FC<WikiCategoryGridProps> = ({
  categories,
  loading = false,
  emptyMessage,
}) => {
  const { t } = useTranslation('wiki');
  const theme = useTheme();
  const upSm = useMediaQuery(theme.breakpoints.up('sm'));
  const upLg = useMediaQuery(theme.breakpoints.up('lg'));

  const gridRef = useRef<HTMLDivElement>(null);
  // Свободная высота под сеткой (px). null — ещё не мерили: рендерим по aspect-ratio, как раньше.
  const [availableHeight, setAvailableHeight] = useState<number | null>(null);
  const [gridWidth, setGridWidth] = useState<number | null>(null);

  const count = categories.length;

  // Динамическое количество колонок: сколько карточек - столько колонок (с ограничениями)
  const smColumns = Math.max(3, Math.min(count, 4));  // на sm-md: минимум 3, максимум 4
  const lgColumns = Math.max(3, Math.min(count, 6));  // на lg+: минимум 3, максимум 6
  const columns = upLg ? lgColumns : upSm ? smColumns : 2;
  const gapPx = (upSm ? SPACING.gridGap.sm : SPACING.gridGap.xs) * 8;

  /**
   * Считаем высоту карточки под реально свободное место.
   *
   * ВАЖНО: у стеклянной подложки (`GlassContainer`) высота НЕ фиксирована — только
   * `maxHeight: calc(100dvh - 200px)` + `overflow:hidden`, т.е. по контенту, но не выше потолка.
   * Поэтому мерить высоту области прокрутки НЕЛЬЗЯ (петля: меньше карточки → ниже подложка → ещё меньше
   * карточки → всё схлопывается в минимум). Меряем ПОТОЛОК: ближайший предок с `max-height` даёт
   * нижнюю границу, верх сетки берём из её позиции (зависит только от крошек и строки поиска, не от карточек).
   */
  const measure = useCallback(() => {
    const node = gridRef.current;
    if (!node) return;

    const scroller = node.closest('.simplebar-content-wrapper') as HTMLElement | null;
    // Позиция верха сетки, не зависящая от текущей прокрутки
    const gridTop = node.getBoundingClientRect().top + (scroller ? scroller.scrollTop : 0);

    // Нижняя граница: край окна либо потолок ближайших предков с max-height (что жёстче).
    // Берём ТОЛЬКО абсолютные потолки в px: у `.simplebar-content-wrapper` стоит `max-height: 100%`,
    // и parseFloat дал бы «100 пикселей» — карточки садились на минимум (уже ловили в проде).
    let limitBottom = window.innerHeight;
    for (let el = node.parentElement; el && el !== document.body; el = el.parentElement) {
      const cs = window.getComputedStyle(el);
      if (!cs.maxHeight.endsWith('px')) continue;
      const cap = parseFloat(cs.maxHeight);
      if (Number.isNaN(cap)) continue;
      // box-sizing: border-box у подложки → из потолка вычитаем её нижние padding + border
      const inner = cap - parseFloat(cs.paddingBottom || '0') - parseFloat(cs.borderBottomWidth || '0');
      limitBottom = Math.min(limitBottom, el.getBoundingClientRect().top + inner);
    }

    setAvailableHeight(Math.max(0, limitBottom - gridTop - HOVER_RESERVE));
    setGridWidth(node.clientWidth);
  }, []);

  useLayoutEffect(() => {
    measure();
  }, [measure, count, columns]);

  // `count` в зависимостях ОБЯЗАТЕЛЕН: на первом рендере категорий ещё нет (грузятся из контекста),
  // сетка не смонтирована и ref пуст. Без count эффект больше не перезапустится — подписки не встанут,
  // и разметка застынет на первом измерении (уже ловили: ресайз окна не пересчитывал карточки).
  useEffect(() => {
    // Ширина сетки и размеры окружения меняются при ресайзе окна и открытии боковой панели.
    // Наблюдаем за родителем сетки, а не за самой сеткой — её высоту мы же и задаём (петля обратной связи).
    const parent = gridRef.current?.parentElement;
    const observer = new ResizeObserver(() => measure());
    if (parent) observer.observe(parent);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, [measure, count, columns]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: { xs: 4, sm: 8 } }}>
        <CircularProgress sx={{ color: '#94a3b8' }} />
      </Box>
    );
  }

  if (categories.length === 0) {
    return (
      <Box sx={{ textAlign: 'center', py: { xs: 4, sm: 8 } }}>
        <Typography sx={{
          color: TEXT_COLORS.MUTED,
          fontSize: { xs: '0.875rem', sm: '1rem' }
        }}>
          {emptyMessage || t('messages.noCategories')}
        </Typography>
      </Box>
    );
  }

  // Итоговая высота карточки = min(естественная по 5/6, влезающая), но не ниже минимума.
  // Считаем в px, а не через CSS `aspectRatio` + `maxHeight`: такая пара ужимает карточке ещё и ШИРИНУ.
  const rows = Math.ceil(count / columns);
  const minCardHeight = upSm ? MIN_CARD_HEIGHT.sm : MIN_CARD_HEIGHT.xs;
  let cardHeight: number | null = null;
  if (availableHeight !== null && gridWidth !== null && gridWidth > 0) {
    const columnWidth = (gridWidth - gapPx * (columns - 1)) / columns;
    const naturalHeight = (columnWidth * 6) / 5;
    const fitHeight = (availableHeight - gapPx * (rows - 1)) / rows;
    cardHeight = Math.floor(Math.max(minCardHeight, Math.min(naturalHeight, fitHeight)));
  }

  return (
    <Box
      ref={gridRef}
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, 1fr)',
          sm: `repeat(${smColumns}, 1fr)`,
          lg: `repeat(${lgColumns}, 1fr)`,
        },
        gap: SPACING.gridGap,
        // Верхний зазор НЕ здесь — им управляет контейнер (строка поиска / крошки),
        // чтобы отступ «бар → сетка» был единым во всех вики-страницах (как на листе, где у сетки pt:0).
      }}
    >
      {categories.map((category) => {
        const showArrow = category.hierarchyColor && !category.hierarchyIsRoot;

        return (
          <Box
            key={`${category.source}-${category.id}`}
            sx={{
              position: 'relative',
              minWidth: 0,
              ...(showArrow && {
                '&::before': {
                  content: '"▸"',
                  position: 'absolute',
                  left: { xs: -4, sm: -12 },
                  top: '50%',
                  transform: 'translate(-50%, -50%)',
                  color: category.hierarchyColor,
                  fontSize: { xs: '14px', sm: '22px' },
                  opacity: 0.7,
                  lineHeight: 1,
                },
              }),
            }}
          >
            <WikiCategoryCard
              name={category.name}
              source={category.source}
              path={category.path}
              height={cardHeight}
              hierarchyColor={category.hierarchyColor}
              hierarchyDepth={category.hierarchyDepth}
              hierarchyHasChildren={category.hierarchyHasChildren}
              hierarchyIsRoot={category.hierarchyIsRoot}
            />
          </Box>
        );
      })}
    </Box>
  );
};

export default WikiCategoryGrid;
